import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";
import { CurrencyForm } from "./CurrencyForm";

interface CurrencyRate {
 _id: string;
 fromCurrency: string;
 toCurrency: string;
 rate: number;
 source: string;
 lastUpdated: string;
 createdAt: string;
}

export const Currencies: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [rates, setRates] = useState<CurrencyRate[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [showForm, setShowForm] = useState(false);
 const [editingId, setEditingId] = useState<string | null>(null);
 const [filterFrom, setFilterFrom] = useState("");
 const [filterTo, setFilterTo] = useState("");

 const fetchRates = useCallback(async () => {
  try {
   setLoading(true);
   setError(null);
   const params = new URLSearchParams();
   if (filterFrom) params.append("fromCurrency", filterFrom.toUpperCase());
   if (filterTo) params.append("toCurrency", filterTo.toUpperCase());
   
   const response = await api.get(`/currencies/admin?${params.toString()}`);
   setRates(response.data.rates || []);
  } catch (err: any) {
   console.error("Failed to fetch currency rates:", err);
   const errorMessage = err.response?.data?.error || err.message || t("admin.currencies.errors.fetch");
   setError(errorMessage);
  } finally {
   setLoading(false);
  }
 }, [api, filterFrom, filterTo, t]);

 useEffect(() => {
  fetchRates();
 }, [filterFrom, filterTo, fetchRates]);

 const handleDelete = async (rateId: string) => {
  if (!window.confirm(t("admin.currencies.confirmDelete"))) {
   return;
  }

  try {
   await api.delete(`/currencies/admin/${rateId}`);
   fetchRates();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.currencies.errors.delete"));
  }
 };

 const handleEdit = (rate: CurrencyRate) => {
  setEditingId(rate._id);
  setShowForm(true);
 };

 const handleFormClose = () => {
  setShowForm(false);
  setEditingId(null);
  fetchRates();
 };

 if (loading && rates.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <Heading size="xl">{t("admin.currencies.title")}</Heading>
     <Button onClick={() => setShowForm(true)}>{t("admin.currencies.addRate")}</Button>
    </div>

    {error && <Alert variant="error">{error}</Alert>}

    <CurrencyForm
     isOpen={showForm}
     rateId={editingId}
     onClose={handleFormClose}
     onSuccess={handleFormClose}
    />

    <Card padding="lg">
     <Stack spacing="md">
      <div className="flex gap-4 items-center">
       <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.currencies.filterFrom")}
        </label>
        <input
         type="text"
         value={filterFrom}
         onChange={(e) => setFilterFrom(e.target.value)}
         placeholder={t("admin.currencies.fromPlaceholder")}
         maxLength={3}
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
        />
       </div>
       <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.currencies.filterTo")}
        </label>
        <input
         type="text"
         value={filterTo}
         onChange={(e) => setFilterTo(e.target.value)}
         placeholder={t("admin.currencies.toPlaceholder")}
         maxLength={3}
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase"
        />
       </div>
      </div>

      <Heading size="lg">{t("admin.currencies.rates")}</Heading>
      {rates.length === 0 ? (
       <div className="text-center text-gray-500 py-8">
        {t("admin.currencies.noRates")}
       </div>
      ) : (
       <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
         <thead className="bg-gray-50">
          <tr>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.currencies.exchangeRate")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.currencies.source")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.currencies.lastUpdated")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.actions")}
           </th>
          </tr>
         </thead>
         <tbody className="bg-white divide-y divide-gray-200">
          {rates.map((rate) => (
           <tr key={rate._id}>
            <td className="px-6 py-4 whitespace-nowrap">
             <button
              onClick={() => handleEdit(rate)}
              className="flex items-center gap-2 text-left hover:opacity-80 transition-opacity cursor-pointer"
              type="button"
             >
              <span className="px-2 inline-flex text-xs leading-5 font-semibold bg-blue-100 text-blue-800">
               {rate.fromCurrency}
              </span>
              <span className="text-sm font-medium text-gray-700">=</span>
              <span className="text-sm font-semibold text-gray-900">{rate.rate.toFixed(4)}</span>
              <span className="text-sm font-medium text-gray-700">×</span>
              <span className="px-2 inline-flex text-xs leading-5 font-semibold bg-green-100 text-green-800">
               {rate.toCurrency}
              </span>
             </button>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
             {rate.source}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
             {new Date(rate.lastUpdated).toLocaleDateString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
             <div className="flex gap-2">
              <Button
               variant="outline"
               size="sm"
               onClick={() => handleEdit(rate)}
              >
               {t("admin.edit")}
              </Button>
              <Button
               variant="outline"
               size="sm"
               onClick={() => handleDelete(rate._id)}
               className="text-red-600 hover:text-red-700"
              >
               {t("admin.delete")}
              </Button>
             </div>
            </td>
           </tr>
          ))}
         </tbody>
        </table>
       </div>
      )}
     </Stack>
    </Card>
   </Stack>
  </Container>
 );
};

export default Currencies;

